import { format } from "date-fns";
import { omit } from "lodash";

import { EXPLORE_MODULE } from "@/constants";
import { datePresets } from "@/helpers/dateRangePicker";
import downloadBlob from "@/helpers/downloadBlob";
import Lang from "@/lang";
import { allControls } from "@/settings/charts/settingsDictionary";
import { metricsDictionary } from "@/settings/metricsDictionary";
import { metricsDictionary as planMetricsDictionary } from "@/settings/planMetricsDictionary";
import store from "@/store";
import { BusinessGoal, Plan, Project, Scenario } from "@/types/project";

const separator = ",";

const csvExport = (data: unknown[][], fileName: string) => {
  const csvContent = data
    .map((row) => row.map((item) => (typeof item === "number" ? item : `"${item}"`)).join(separator))
    .join("\n");
  downloadBlob(csvContent, `${fileName}.csv`);
};

export const buildFileName = (title: string) => [title, format(new Date(), "yyyy-MM-dd")].join(" - ");

export const buildHeaderRows = (title: string) => [
  [title],
  ["Generation Date", format(new Date(), "yyyy-MM-dd hh:mm:ss a")],
];

export const buildFilterRows = (body: Record<string, unknown>, isSingleChart = true) => {
  const csvContent: string[][] = [];

  if (!isSingleChart) {
    return csvContent;
  }

  const filterParams = store.getters["userSettings/filterParams"];
  const isPreset = filterParams.dateRange.type === "preset";
  if (isPreset) {
    body = {
      ...omit(body, ["start_time", "end_time"]),
      dateRange: datePresets.find((item) => item.value === filterParams.dateRange.value.preset)?.text,
    };
  } else {
    body = {
      ...omit(body, ["start_time", "end_time"]),
      dateRange: [body.start_time as string, body.end_time as string].filter((item) => item !== undefined).join("---"),
    };
  }

  if (isSingleChart) {
    csvContent.push([]);
    Object.keys(body).forEach((key) => {
      const value = Array.isArray(body[key]) ? (body[key] as string[]).join(", ") : body[key];
      const filterName =
        metricsDictionary.find((item) => item.value === key)?.title || (Lang as Record<string, unknown>)[key];
      csvContent.push([filterName as string, value as string]);
    });
  }

  return csvContent;
};

export const buildControlRows = (controls: Record<string, string | number | boolean | object>) => {
  const csvContent = [];

  csvContent.push([]);
  Object.keys(controls).forEach((key) => {
    csvContent.push([
      (allControls.find((item) => (item as Record<string, unknown>).name === key) as Record<string, unknown>)?.label,
      controls[key],
    ]);
  });

  return csvContent;
};

export const getDataHeaders = (data: Record<string, unknown>[]) => {
  const dataKeys: Set<string> = new Set();
  data.forEach((item: Record<string, unknown>) => {
    Object.keys(item).forEach((key) => (dataKeys as Set<string>).add(key));
  });
  return Array.from(dataKeys) as string[];
};

export const buildDataHeaderRows = (data: Record<string, unknown>[], module: string = EXPLORE_MODULE) => {
  const csvContent: string[][] = [];

  let dataKeys: Set<string> | string[] = new Set();
  data.forEach((item: Record<string, unknown>) => {
    Object.keys(item).forEach((key) => (dataKeys as Set<string>).add(key));
  });
  dataKeys = Array.from(dataKeys) as string[];

  const dict = module === EXPLORE_MODULE ? metricsDictionary : planMetricsDictionary;
  csvContent.push(dataKeys.map((key) => dict.find((item) => item.value === key)?.title || key));

  return csvContent;
};

export const buildDataTableRows = (data: Record<string, unknown>[], headers: string[]) => {
  const csvContent: string[][] = [];

  data.forEach((item: Record<string, unknown>) => {
    const row: string[] = [];
    headers.forEach((dataKey) => {
      row.push((item[dataKey] ?? null) as string);
    });
    csvContent.push(row);
  });

  return csvContent;
};

export const buildFullDataTableRows = (data: Record<string, unknown>[], module: string = EXPLORE_MODULE) => {
  const headers = getDataHeaders(data);

  const csvContent: string[][] = [[]];
  csvContent.push(...buildDataHeaderRows(data, module));
  csvContent.push(...buildDataTableRows(data, headers));

  return csvContent;
};

export const buildProjectExportRows = (projects: Project[]): Record<string, string>[] => {
  const clean = (val: unknown): string => (val === null || val === undefined || val === "null" ? "" : String(val));

  const rows: Record<string, string>[] = [];

  type ExportContext = {
    Project: string;
    "Business Goal": string;
    Plan: string;
    Scenario: string;
    Agency: string;
    Advertiser: string;
    "Owned By": string;
    "Business Goal Description": string;
    "Is Approved": string;
    "Publish Date": string;
    "Last Modified": string;
  };

  const emptyContext = (): ExportContext => ({
    Project: "",
    "Business Goal": "",
    Plan: "",
    Scenario: "",
    Agency: "",
    Advertiser: "",
    "Owned By": "",
    "Business Goal Description": "",
    "Is Approved": "",
    "Publish Date": "",
    "Last Modified": "",
  });

  const getStr = (o: unknown, key: string): string => {
    if (o && typeof o === "object" && key in (o as Record<string, unknown>)) {
      const v = (o as Record<string, unknown>)[key];
      return v == null || v === "null" ? "" : String(v);
    }
    return "";
  };

  const getBool = (o: unknown, key: string): boolean => {
    if (o && typeof o === "object" && key in (o as Record<string, unknown>)) {
      const v = (o as Record<string, unknown>)[key];
      return Boolean(v);
    }
    return false;
  };

  const hasArray = (o: unknown, key: string): o is Record<string, unknown[]> => {
    return !!(o && typeof o === "object" && Array.isArray((o as Record<string, unknown>)[key]));
  };

  type Level = "project" | "goal" | "plan" | "scenario";
  type LevelMap = {
    project: Project;
    goal: BusinessGoal;
    plan: Plan;
    scenario: Scenario;
  };

  function traverse<L extends Level>(level: L, data: LevelMap[L], context: ExportContext): void {
    if (level === "project") {
      const proj = data as LevelMap["project"];
      const row: ExportContext = {
        Project: clean((proj as unknown as Record<string, unknown>)["name"]),
        "Business Goal": "",
        Plan: "",
        Scenario: "",
        Agency: getStr(proj, "agency_name"),
        Advertiser: getStr(proj, "advertiser_id"),
        "Owned By": getStr(proj, "owned_by"),
        "Business Goal Description": "",
        "Is Approved": "",
        "Publish Date": getStr(proj, "publish_date") ? new Date(getStr(proj, "publish_date")).toLocaleDateString() : "",
        "Last Modified": getStr(proj, "last_modified") ? new Date(getStr(proj, "last_modified")).toLocaleString() : "",
      };
      rows.push({ ...row });

      if (hasArray(proj, "business_goals")) {
        (proj as unknown as Record<string, unknown[]>).business_goals.forEach((g) =>
          traverse("goal", g as BusinessGoal, {
            ...row,
            "Business Goal": getStr(g, "name"),
            "Business Goal Description": getStr(g, "business_goal"),
          })
        );
      }
    } else if (level === "goal") {
      const goal = data as LevelMap["goal"];
      rows.push({ ...context });

      if (hasArray(goal, "plans")) {
        (goal as unknown as Record<string, unknown[]>).plans.forEach((p) =>
          traverse("plan", p as Plan, {
            ...context,
            Plan: getStr(p, "name"),
            "Is Approved": getBool(p, "approved") ? "true" : "",
          })
        );
      }
    } else if (level === "plan") {
      const plan = data as LevelMap["plan"];
      rows.push({ ...context });

      if (hasArray(plan, "scenarios")) {
        (plan as unknown as Record<string, unknown[]>).scenarios.forEach((s) =>
          traverse("scenario", s as Scenario, {
            ...context,
            Scenario: getStr(s, "name"),
            "Is Approved": getBool(s, "approved") ? "true" : "",
          })
        );
      }
    } else {
      rows.push({ ...context });
    }
  }

  projects.forEach((project) => traverse("project", project, emptyContext()));
  return rows;
};

export default csvExport;
